import React from 'react';
import { Badge } from 'reactstrap';
import { queueStatePresentation } from './queueState';
import InvoiceDetailsLink from './InvoiceDetailsLink';

// Hold times follow US Eastern business hours (as in the API's emails), so show them in ET for every viewer.
const DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
});

export const formatDateTime = (value) => (value ? DATE_TIME_FORMAT.format(new Date(value)) : '');

const formatAmount = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// An empty label (e.g. a released row with no approver) renders nothing rather than an empty pill.
export const StateBadge = ({ label, color, outline }) => (
  !label ? null : outline
    // reactstrap 8 Badge has no `outline`; use Bootstrap utilities (theme-independent colours).
    ? <span className={`badge bg-transparent border border-${color} text-${color} funding-badge`}>{label}</span>
    : <Badge color={color} className="funding-badge">{label}</Badge>
);

// Sortable column ids equal the API `sortby` whitelist keys (program_name, invoice_number, amount,
// held_at, release_at); everything else is disableSortBy so no dead sort arrow is shown.
export const COLUMNS = [
  {
    Header: 'Program',
    id: 'program_name',
    width: 180,
    minWidth: 150,
    accessor: 'program_name',
    Cell: ({ row }) => `${row.original.program_name ?? ''}${row.original.program_deleted ? ' (deleted)' : ''}`,
  },
  {
    // There is no standalone invoice route; the number opens the Invoice Details modal.
    Header: 'Invoice #',
    id: 'invoice_number',
    width: 120,
    minWidth: 90,
    accessor: 'invoice_number',
    // onInvoiceClosed is passed to useTable by the grid (react-table hands table options to cells).
    Cell: ({ row, value, onInvoiceClosed }) => (
      <InvoiceDetailsLink
        organizationId={row.original.organization_id}
        programId={row.original.invoice_program_id ?? row.original.program_id}
        invoiceId={row.original.invoice_id}
        deleted={Boolean(row.original.invoice_deleted || row.original.program_deleted)}
        onClosed={onInvoiceClosed}
      >
        {value}
      </InvoiceDetailsLink>
    ),
  },
  {
    Header: 'Amount',
    id: 'amount',
    width: 110,
    minWidth: 80,
    accessor: 'amount',
    Cell: ({ value }) => formatAmount(value),
  },
  {
    Header: 'Funding Method',
    id: 'funding_method',
    width: 140,
    minWidth: 110,
    accessor: 'funding_method',
    disableSortBy: true,
  },
  {
    Header: 'Payment Applied',
    id: 'payment_applied',
    width: 170,
    minWidth: 140,
    accessor: 'held_at',
    Cell: ({ value }) => formatDateTime(value),
    disableSortBy: true,
  },
  {
    Header: 'Applied By',
    id: 'applied_by',
    width: 170,
    minWidth: 140,
    accessor: 'applied_by',
    disableSortBy: true,
  },
  {
    Header: 'Hold Start',
    id: 'held_at',
    width: 170,
    minWidth: 140,
    accessor: 'held_at',
    Cell: ({ value }) => formatDateTime(value),
  },
  {
    Header: 'Hold Expires',
    id: 'release_at',
    width: 170,
    minWidth: 140,
    accessor: 'release_at',
    Cell: ({ value }) => formatDateTime(value),
  },
  {
    Header: 'Hold Status',
    id: 'hold_status',
    width: 200,
    minWidth: 170,
    accessor: 'queue_state',
    Cell: ({ row }) => {
      const p = queueStatePresentation(row.original);
      return <StateBadge label={p.holdLabel} color={p.holdColor} outline={p.holdOutline} />;
    },
    disableSortBy: true,
  },
  {
    Header: 'Approval Status',
    id: 'approval_status',
    width: 220,
    minWidth: 190,
    accessor: 'approved_by',
    Cell: ({ row }) => {
      const p = queueStatePresentation(row.original);
      return <StateBadge label={p.approvalLabel} color={p.approvalColor} outline={p.approvalOutline} />;
    },
    disableSortBy: true,
  },
];
